import React, { useState, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, Trash2, X, Loader2 } from "lucide-react";
import {
  resolveCodeProjects,
  makeProjectId,
  CODE_PROJECT_COLORS,
} from "../../config/codeProjects";
import { saveCodeProjects } from "../../services/workspaceService";
import deskGlass, { ACCENT } from "../../styles/deskGlass";
import "./codeProjectsEditor.css";

const styles = deskGlass;

function SortableRow({ project, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: project.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="code-proj-row">
      <button type="button" className="code-proj-grip" {...attributes} {...listeners} aria-label="Reordenar">
        <GripVertical size={16} />
      </button>
      <input
        className="code-proj-input"
        value={project.label}
        onChange={(e) => onChange(project.id, { label: e.target.value, notebookName: e.target.value })}
        placeholder="nombre del proyecto"
      />
      <div className="code-proj-colors">
        {CODE_PROJECT_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            className={`code-proj-swatch${project.color === c ? " is-active" : ""}`}
            style={{ background: c }}
            onClick={() => onChange(project.id, { color: c })}
            aria-label={`Color ${c}`}
          />
        ))}
      </div>
      <button type="button" className="code-proj-remove" onClick={() => onRemove(project.id)} aria-label="Quitar">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

export default function CodeProjectsEditor({ isOpen, onClose, settings }) {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (isOpen) {
      setItems(resolveCodeProjects(settings));
      setNewName("");
      setError(null);
    }
  }, [isOpen, settings]);

  const persist = useCallback(async (next) => {
    setSaving(true);
    setError(null);
    try {
      await saveCodeProjects(next);
    } catch (err) {
      console.error("[CodeProjectsEditor]", err);
      setError(err.message || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }, []);

  const updateItem = (id, patch) => {
    setItems((prev) => {
      const next = prev.map((p) => (p.id === id ? { ...p, ...patch } : p));
      persist(next);
      return next;
    });
  };

  const removeItem = (id) => {
    setItems((prev) => {
      const next = prev.filter((p) => p.id !== id);
      persist(next);
      return next;
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((p) => p.id === active.id);
      const newIndex = prev.findIndex((p) => p.id === over.id);
      const next = arrayMove(prev, oldIndex, newIndex).map((p, i) => ({ ...p, order: i }));
      persist(next);
      return next;
    });
  };

  const addProject = () => {
    const label = newName.trim();
    if (!label) return;
    const id = makeProjectId(label, items);
    const color = CODE_PROJECT_COLORS[items.length % CODE_PROJECT_COLORS.length];
    const entry = { id, label, notebookName: label, color, order: items.length };
    const next = [...items, entry];
    setItems(next);
    setNewName("");
    persist(next);
  };

  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div className="code-proj-modal" onClick={(e) => e.stopPropagation()}>
        <div style={styles.glassHeader}>
          <h2 style={styles.glassTitle}>Proyectos de código</h2>
          <button type="button" onClick={onClose} className="code-proj-close" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <p style={{ ...styles.subtext, margin: "0 0 12px" }}>
          Chips rápidos en Hoy › Código. Arrastrá para ordenar. Cada uno = cuaderno en Temas › Código.
        </p>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((p) => p.id)} strategy={verticalListSortingStrategy}>
            <div className="code-proj-list">
              {items.map((p) => (
                <SortableRow key={p.id} project={p} onChange={updateItem} onRemove={removeItem} />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <div className="code-proj-add">
          <input
            className="code-proj-input code-proj-input--add"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addProject()}
            placeholder="Nuevo proyecto…"
          />
          <button type="button" className="code-proj-add-btn" onClick={addProject} disabled={!newName.trim() || saving}>
            <Plus size={16} color={ACCENT} />
            Agregar
          </button>
        </div>

        {saving && (
          <p style={{ ...styles.subtext, marginTop: "10px", display: "flex", alignItems: "center", gap: "6px" }}>
            <Loader2 size={14} className="animate-spin" /> Guardando…
          </p>
        )}
        {error && <p style={{ color: "#ffb4b4", fontSize: "0.85rem", marginTop: "8px" }}>{error}</p>}
      </div>
    </div>
  );
}
